import React, { useState, useMemo, useEffect, Suspense } from "react";
import { Link } from "react-router-dom";
import { ErrorBoundary } from 'react-error-boundary';
import {
  ChevronRight, Search, Stethoscope, Syringe, Sparkles, Droplet, Leaf,
  ArrowRight, ShieldCheck, Truck, Headphones
} from 'lucide-react';
import categoriesHeroImg from "../assets/categories-hero.png";
import categoriesBrandImg from "../assets/categories2.png";
import { useParentCategories } from "../hooks/useCategoriesQuery";
import { LoadingSkeleton, ErrorFallback } from '../components/ui/SuspenseComponents';
import Pagination from '../components/ui/Pagination';
import { getImageUrl } from '../lib/utils';

const CATEGORIES_PER_PAGE = 20;

// Rotating color themes + fallback icons so each category card gets its own identity
const CATEGORY_THEMES = [
  { bg: "from-blue-50 to-blue-100/60", text: "text-blue-600", border: "border-blue-200", hoverBg: "hover:bg-blue-50", icon: Stethoscope },
  { bg: "from-emerald-50 to-emerald-100/60", text: "text-emerald-600", border: "border-emerald-200", hoverBg: "hover:bg-emerald-50", icon: Syringe },
  { bg: "from-rose-50 to-rose-100/60", text: "text-rose-600", border: "border-rose-200", hoverBg: "hover:bg-rose-50", icon: Sparkles },
  { bg: "from-amber-50 to-amber-100/60", text: "text-amber-600", border: "border-amber-200", hoverBg: "hover:bg-amber-50", icon: Droplet },
  { bg: "from-indigo-50 to-indigo-100/60", text: "text-indigo-600", border: "border-indigo-200", hoverBg: "hover:bg-indigo-50", icon: Leaf },
];

// Main content component that uses the hook
const AllCategoriesContent = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { data: categories } = useParentCategories();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeAlphabetFilter, setActiveAlphabetFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // Generate alphabet for filter
  const alphabet = useMemo(() => {
    return Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i));
  }, []);

  // Function to check if any categories start with a given letter
  const hasCategories = (letter) => {
    return categories.some((category) =>
      category.name.toUpperCase().startsWith(letter)
    );
  };

  // Handle alphabet filter selection
  const handleAlphabetFilter = (letter) => {
    if (activeAlphabetFilter === letter) {
      // If clicking the same letter, clear filter
      setActiveAlphabetFilter("");
    } else {
      setActiveAlphabetFilter(letter);
      // Clear search query when using alphabet filter
      setSearchQuery("");
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setActiveAlphabetFilter("");
    setSearchQuery("");
  };

  // Filter categories based on search query and alphabet filter
  const filteredCategories = useMemo(() => {
    return categories.filter((category) => {
      const matchesSearch = searchQuery
        ? category.name.toLowerCase().includes(searchQuery.toLowerCase())
        : true;

      const matchesAlphabet = activeAlphabetFilter
        ? category.name.toUpperCase().startsWith(activeAlphabetFilter)
        : true;

      return matchesSearch && matchesAlphabet;
    });
  }, [categories, searchQuery, activeAlphabetFilter]);

  // Reset to first page whenever the filtered set changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, activeAlphabetFilter]);

  const totalPages = Math.ceil(filteredCategories.length / CATEGORIES_PER_PAGE);
  const paginatedCategories = filteredCategories.slice(
    (currentPage - 1) * CATEGORIES_PER_PAGE,
    currentPage * CATEGORIES_PER_PAGE
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Category Card Component
  const CategoryCard = ({ category, theme }) => {
    const ThemeIcon = theme.icon;
    return (
      <Link
        to={`/category/${category.slug}`}
        className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 overflow-hidden flex flex-col"
      >
        <div className={`relative ${category.image ? '' : `bg-gradient-to-br ${theme.bg} flex items-center justify-center`} h-36 sm:h-44 overflow-hidden`}>
          {category.image ? (
            <img
              src={getImageUrl(category.image)}
              alt={category.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-white shadow-md flex items-center justify-center">
              <ThemeIcon className={`w-9 h-9 sm:w-10 sm:h-10 ${theme.text}`} strokeWidth={1.5} />
            </div>
          )}
          {category.featured && (
            <span className="absolute top-3 right-3 text-2xs font-medium bg-primary-100 text-primary-700 py-0.5 px-2.5 rounded-full">
              Featured
            </span>
          )}
        </div>
        <div className="p-4 sm:p-5 flex flex-col items-center text-center flex-1">
          <h3 className={`text-base sm:text-lg font-heading font-bold ${theme.text} mb-1`}>
            {category.name}
          </h3>
          <p className="text-xs sm:text-sm text-gray-500 mb-4 line-clamp-2">
            {category.description || `Browse ${category.name} products and supplies`}
          </p>
          <span className={`mt-auto inline-flex items-center gap-1 text-xs sm:text-sm font-semibold border ${theme.border} ${theme.text} rounded-full px-4 py-1.5 ${theme.hoverBg} transition-colors`}>
            Explore {category.name}
            <ArrowRight className="w-3.5 h-3.5" />
          </span>
        </div>
      </Link>
    );
  };

  return (
    <div className="w-full bg-white">
      {/* Breadcrumb */}
      <div className="container mx-auto px-4 pt-4">
        <nav className="flex items-center gap-1.5 text-xs text-gray-500">
          <Link to="/" className="hover:text-gray-700 transition-colors">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-secondary-600 font-medium">Categories</span>
        </nav>
      </div>

      {/* Hero */}
      <div className="bg-gradient-to-br from-red-50 via-white to-white">
        <div className="container mx-auto px-4 py-8 md:py-12">
          <div className="grid md:grid-cols-2 gap-6 md:gap-8 items-center">
            <div className="order-2 md:order-1">
              <h1 className="text-2xl md:text-4xl font-semibold text-gray-900 leading-tight mb-3">
                Main <span className="text-red-600">Product</span> Categories
              </h1>
              <p className="text-sm md:text-base text-gray-500 max-w-md leading-relaxed">
                Explore <span className="text-blue-600 font-medium">dental</span>,{" "}
                <span className="text-emerald-600 font-medium">surgical</span>, and{" "}
                <span className="text-red-600 font-medium">skincare</span> products
                for professionals and everyday care.
              </p>
            </div>

            <div className="flex justify-center order-1 md:order-2">
              <div className="w-full max-w-[260px] md:max-w-sm rounded-2xl overflow-hidden shadow-sm">
                <img
                  src={categoriesHeroImg}
                  alt="Dental instruments and skincare products"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and A-Z Filter */}
      <div className="sticky top-[84px] md:top-[92px] bg-white shadow-sm border-y border-gray-100 z-40">
        <div className="container mx-auto px-4 py-4">
          {/* Search Bar */}
          <div className="relative mb-3">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                if (e.target.value) setActiveAlphabetFilter("");
              }}
              placeholder="Search categories..."
              className="w-full py-2.5 pl-10 pr-4 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-1 focus:ring-red-400 focus:border-red-400 transition-colors"
            />
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400 pointer-events-none">
              <Search className="w-4 h-4" />
            </div>
          </div>

          {/* A-Z Filter */}
          <div className="flex flex-wrap items-center gap-1 md:gap-1.5">
            <button
              onClick={clearFilters}
              className={`text-xs md:text-sm py-1 px-2.5 rounded-full font-medium transition-colors ${
                !activeAlphabetFilter && !searchQuery
                  ? "bg-red-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              All
            </button>

            {alphabet.map((letter) => {
              const hasMatchingCategories = hasCategories(letter);
              const isActive = activeAlphabetFilter === letter;
              return (
                <button
                  key={letter}
                  onClick={() =>
                    hasMatchingCategories && handleAlphabetFilter(letter)
                  }
                  disabled={!hasMatchingCategories}
                  className={`text-xs md:text-sm py-1 px-2 rounded-full font-medium transition-colors ${
                    isActive
                      ? "bg-red-600 text-white"
                      : hasMatchingCategories
                      ? "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      : "bg-gray-100 text-gray-400 opacity-40 cursor-not-allowed"
                  }`}
                >
                  {letter}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Categories Grid */}
      <div className="container mx-auto px-4 py-6 sm:py-8 md:py-10">
        {filteredCategories.length > 0 ? (
          <>
            {/* Title with Count */}
            <h2 className="text-xl md:text-2xl font-heading font-bold text-[#1B365D] mb-1">
              {activeAlphabetFilter
                ? `Explore Our Categories: ${activeAlphabetFilter}`
                : "Explore Our Categories"}
              <span className="text-base font-normal text-secondary-600 ml-2">
                ({filteredCategories.length})
              </span>
            </h2>
            <div className="w-12 h-0.5 bg-secondary-600 mb-5" />

            {/* Categories Grid - Mobile optimized layout */}
            <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
              {paginatedCategories.map((category, idx) => (
                <CategoryCard
                  key={category._id}
                  category={category}
                  theme={CATEGORY_THEMES[idx % CATEGORY_THEMES.length]}
                />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="mt-8">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <div className="mx-auto h-16 w-16 mb-4 text-admin-slate-400 flex items-center justify-center">
              <svg
                className="w-full h-full"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-heading font-medium text-admin-slate-800 mb-2">
              No Categories Found
            </h3>
            <p className="text-admin-slate-500 mb-6">
              {activeAlphabetFilter
                ? `We couldn't find any categories starting with '${activeAlphabetFilter}'.`
                : `We couldn't find any product categories that match your search.`}
            </p>
            <button
              onClick={clearFilters}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-full shadow-sm text-sm font-medium text-white bg-secondary-600 hover:bg-secondary-700 active:bg-secondary-800 transition duration-300"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Brand CTA Section */}
      <div className="container mx-auto px-4 pb-10">
        <div className="bg-admin-slate-50 rounded-2xl p-6 sm:p-8 flex flex-col md:flex-row items-center gap-8 md:gap-12">
          {/* Brand panel */}
          <div className="relative w-full max-w-[220px] md:max-w-[240px] flex-shrink-0">
            <div className="relative aspect-square rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <img
                src={categoriesBrandImg}
                alt="CureHive Nepal branded products"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <div className="flex-1 text-center md:text-left">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-[#1B365D] mb-1">
              Everything You Need for <span className="text-secondary-600">Better Care</span>
            </h2>
            <div className="w-12 h-0.5 bg-secondary-600 mb-4 mx-auto md:mx-0" />
            <p className="text-sm md:text-base text-gray-600 mb-6">
              CureHive Nepal brings together trusted products across dental,
              surgical, and skincare categories in one convenient marketplace.
              Whether you're a healthcare professional, clinic, pharmacy, or
              individual customer, explore carefully selected products from
              trusted brands.
            </p>

            <div className="grid grid-cols-1 xs:grid-cols-3 gap-4">
              {[
                { icon: ShieldCheck, title: "Trusted Quality", desc: "Carefully selected products", color: "text-secondary-600 bg-secondary-50" },
                { icon: Truck, title: "Fast & Reliable", desc: "Quick delivery across Nepal", color: "text-emerald-600 bg-emerald-50" },
                { icon: Headphones, title: "Expert Support", desc: "We're here to help you always", color: "text-primary-700 bg-primary-50" },
              ].map((badge) => {
                const BadgeIcon = badge.icon;
                return (
                  <div key={badge.title} className="flex items-center gap-3 justify-center md:justify-start">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${badge.color}`}>
                      <BadgeIcon className="w-5 h-5" strokeWidth={1.5} />
                    </div>
                    <div className="text-left">
                      <p className="text-xs sm:text-sm font-semibold text-[#1B365D]">{badge.title}</p>
                      <p className="text-2xs sm:text-xs text-gray-500">{badge.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main component with Suspense and Error Boundary
const AllCategoriesPage = () => {
  return (
    <ErrorBoundary
      FallbackComponent={({ error, resetErrorBoundary }) => (
        <div className="w-full bg-white">
          <div className="bg-gradient-to-br from-red-50 via-white to-white">
            <div className="container mx-auto px-4 py-8 md:py-12">
              <h1 className="text-2xl md:text-4xl font-semibold text-gray-900 leading-tight mb-3">
                Main <span className="text-red-600">Product</span> Categories
              </h1>
              <p className="text-sm md:text-base text-gray-500 max-w-md leading-relaxed">
                Explore dental, surgical, and skincare products for professionals and everyday care.
              </p>
            </div>
          </div>
          <div className="container mx-auto px-4 py-8">
            <ErrorFallback error={error} resetErrorBoundary={resetErrorBoundary} type="categories" />
          </div>
        </div>
      )}
      onReset={() => window.location.reload()}
    >
      <Suspense fallback={<LoadingSkeleton type="categories" />}>
        <AllCategoriesContent />
      </Suspense>
    </ErrorBoundary>
  );
};

export default AllCategoriesPage;
